import React, { forwardRef, useState, useEffect } from 'react';
import './IntroSection.css';
import { FaLinkedin } from 'react-icons/fa';
import { collection, getDocs, getDoc, doc, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase/firebase-config';

/* **word** → highlighted span */
const toHtml = (text) =>
  (text || "").replace(/\*\*(.*?)\*\*/g, '<span class="highlight">$1</span>');

const normalizePointText = (data = {}) => {
  const source = data.text || data.body || data.content || data.description || data.html || "";
  return String(source)
    .replace(/<span class="highlight">(.*?)<\/span>/g, "**$1**")
    .replace(/<[^>]+>/g, "")
    .trim();
};

/* Fallback static content — shown until Firestore loads */
const STATIC_POINTS = [
  "With 5 years of experience in **Contract Management**, I've worked across **startups**, **scaleups**, and **global teams**, drafting, reviewing, and negotiating contracts that drive clarity, manage risk, and protect business interests.",
  "From **vendor and service agreements** to **procurement contracts**, **privacy addendums**, and cross-functional legal documents, I bring a **strategic mindset** and sharp communication skills to every stage of the contract lifecycle.",
  "Beyond contract work, I've driven business growth through **legal recruiting**, optimizing contract workflows, engaging on social media, and supporting investor outreach and networking for early-stage companies.",
  "This site is a curated extension of that work, created to go beyond the bullet points of a CV. It offers insight into my approach, showcases the work behind my results, and reflects the standards I bring to every engagement: **clarity**, **consistency**, and **care**.",
  "Explore the sections below to uncover insights from my **career**, **skills**, real-world cases, practical tips, and more.",
];
const STATIC_CONFIG = {
  headingPrefix: "Get to know",
  headingAccent: "me",
  linkedinUrl:   "https://www.linkedin.com/in/pooja-biswal-5ab931112/",
  linkedinLabel: "Connect with me on LinkedIn",
  introPhoto: "https://firebasestorage.googleapis.com/v0/b/pp-studio-website.firebasestorage.app/o/library%2Fhero-bg%2Fhero.jpg?alt=media&token=a4190605-7be8-4a6e-b7fe-abf5703fa6bb",
};

const IntroSection = forwardRef((props, ref) => {
  const [points, setPoints] = useState(STATIC_POINTS);
  const [cfg,    setCfg]    = useState(STATIC_CONFIG);

  useEffect(() => {
    (async () => {
      try {
        const [pSnap, cSnap] = await Promise.all([
          getDocs(query(collection(db,"intro_points"), orderBy("order","asc"))),
          getDoc(doc(db,"intro_config","main")),
        ]);
        if (!pSnap.empty) {
          const remotePoints = pSnap.docs
            .map(d => normalizePointText(d.data()))
            .filter(Boolean);

          setPoints(remotePoints.length ? remotePoints : STATIC_POINTS);
        }
        if (cSnap.exists()) {
          setCfg(prev => ({ ...prev, ...cSnap.data() }));
        }
      } catch(_) { /* silently keep static fallback */ }
    })();
  }, []);

  return (
    <section className="intro-section" ref={ref}>
      <div className="intro-frame">
        <div className="intro-body">
          {cfg.introPhoto && (
            <div className="intro-photo-col">
              <div className="intro-photo-frame">
                <img src={cfg.introPhoto} alt="Pooja Biswal" loading="lazy" />
              </div>
            </div>
          )}

          <div className="intro-content-col">
            <h2 className="intro-heading">
              <span className="intro-accent" aria-hidden="true"></span>
              {cfg.headingPrefix} <em>{cfg.headingAccent}</em>
            </h2>

            <ul className="intro-points">
              {points.map((html, idx) => (
                <li
                  className="intro-point fade-in-up"
                  key={idx}
                  tabIndex="0"
                  style={{ animationDelay: `${idx * 0.15 + 0.2}s` }}
                >
                  <span className="point-badge" aria-label={`Point ${idx + 1}`}>
                    {idx + 1}
                  </span>
                  <span className="arrow-anim" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="24" height="24" fill="none">
                      <path
                        d="M5 12h14M13 6l6 6-6 6"
                        stroke="#C25B2A"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span
                    className="point-text"
                    dangerouslySetInnerHTML={{ __html: toHtml(html) }}
                  />
                </li>
              ))}
            </ul>

            <div className="linkedin-cta">
              <a
                href={cfg.linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="linkedin-btn"
                aria-label={cfg.linkedinLabel}
              >
                <FaLinkedin className="linkedin-icon" aria-hidden="true" />
                {cfg.linkedinLabel}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

IntroSection.displayName = 'IntroSection';

export default IntroSection;
